<script lang="ts" context="module">
    import type { EditorLayout, SplitLayout, SubLayoutState } from "../editor";
    import { get, type Writable } from "svelte/store";

    export function createSplitPanelLayout(id: string, layout: EditorLayout) {
        const splitLayout = layout.subLayouts[id] as SplitLayout & { state: Writable<SubLayoutState> };
        if (!splitLayout) throw new Error(`No split panel layout with id '${id}' found.`);

        const initial = splitLayout.initial.map((id, _, array) => ({ id, size: 1 / array.length }));
        const state = layout.state.create(id, initial);

        const minSize = splitLayout.options.minSize ?? 16 * 2.5;

        function insert(index: number, item: string) {
            let size = 0;
            state.update((state) => {
                size = state.reduce((prev, r) => prev + r.size, 0) / (state.length + 1);
                state.forEach(item => item.size -= size / state.length);
                state.splice(index, 0, { id: item, size });
                return state;
            });
        }

        function remove(index: number) {
            state.update((state) => {
                if (state.length === 1) return state;
                const size = state[index].size;
                state.forEach(item => item.size += size / (state.length - 1));
                state.splice(index, 1);
                return state;
            });
        }

        function active(items: string[]) {
            const s = get(state);

            const active = items.map(_ => false);
            let target = 0;
            for (let i = 0; i < items.length; i++) {
                if (!s[target]) break;
                if (s[target].id === items[i]) {
                    active[i] = true;
                    target++;
                }
            }

            return active;
        }

        function toggleItems(item: string, items: string[]) {
            const s = get(state);
            let target = 0;

            // calculate target
            const index = items.indexOf(item);
            for (let i = 0; i < index; i++) {
                if (!s[target]) break;
                if (s[target].id === items[i]) {
                    target++;
                }
            }

            // update state
            if (s[target]?.id === item) {
                remove(target);
            } else {
                insert(target, item);
            }

            return active(items);
        }

        function toggle(item: string) {
            const s = get(state);
            const index = s.findIndex(i => i.id === item);
            if (index === -1) {
                insert(s.length, item);
            } else {
                remove(index);
            }
        }

        function open(item: string) {
            const s = get(state);
            const index = s.findIndex(i => i.id === item);
            if (index !== -1) return;

            insert(s.length, item);
        }

        function close(item: string) {
            const s = get(state);
            const index = s.findIndex(i => i.id === item);
            if (index === -1) return;

            remove(index);
        }

        splitLayout.state.set({
            toggleItems,
            toggle,
            active,
            open,
            close
        });

        return {
            state,
            direction: splitLayout.direction,
            minSize
        };
    }
</script>

<script lang="ts">
    import { getLayout } from "../Layout.svelte";
    import PanelTemplate from "../PanelTemplate.svelte";

    const { panel, layout: editorLayout } = getLayout();
    export let id: string;
    let clazz: string = "";
    export { clazz as class };

    const splitLayout = createSplitPanelLayout(id, editorLayout);
    const { state, minSize } = splitLayout;

    /////////////////////////////////////////////

    function createTemplate(s: any) {
        return $state.map(({ size }, i) => `"a${i}" ${size}fr`).join(" ") + " / 1fr";
    }

    function resizeStart(e: PointerEvent, index: number) {
        let totalSize = $state[index].size + $state[index + 1].size;
        const elem = e.target as HTMLElement;

        const prev = (elem.previousElementSibling as HTMLElement).getBoundingClientRect().top;
        const next = (elem.nextElementSibling as HTMLElement).getBoundingClientRect().top;

        const totalElemSize = next - prev;
        const elemMinSize = (minSize / totalElemSize);


        elem.setPointerCapture(e.pointerId);
        elem.addEventListener("pointermove", resizeMove);
        elem.addEventListener("pointerup", resizeEnd);
        elem.addEventListener("pointercancel", resizeEnd);


        function resizeMove(e: PointerEvent) {
            const ratio = Math.min(Math.max((e.clientY - prev) / totalElemSize, elemMinSize), 1 - elemMinSize);

            $state[index].size = totalSize * ratio;
            $state[index + 1].size = totalSize * (1 - ratio);
            $state = $state;
        }

        function resizeEnd(e: PointerEvent) {
            elem.removeEventListener("pointermove", resizeMove);
            elem.removeEventListener("pointerup", resizeEnd);
            elem.removeEventListener("pointercancel", resizeEnd);
        }
    }
</script>

<!----------------------------------------------------------------------------->

<div class="layout {clazz}" style="grid-template: {createTemplate($state)}; grid-area: {id};">

  {#each $state as { id }}
    {@const item = panel(id)}
    {#if item}
      <div class="panel">
        <PanelTemplate panel={item}/>
      </div>
    {:else}
      <span>Unknown panel: {id}</span>
    {/if}
  {/each}

  <div class="top" />
  {#each { length: $state.length - 1 } as _, i}
    <div class="handle" style="grid-area: a{i}" on:pointerdown={(e) => resizeStart(e, i)} />
  {/each}
  <div class="bottom" />
</div>

<!----------------------------------------------------------------------------->

<style>
    .layout {
        display: grid;
        width: 100%;
        height: 100%;
        background: var(--panel-bg);
    }

    .layout > :global(:nth-child(1)) {
        grid-area: a0;
    }

    .layout > :global(:nth-child(2)) {
        grid-area: a1;
    }

    .layout > :global(:nth-child(3)) {
        grid-area: a2;
    }

    .layout > :global(:nth-child(4)) {
        grid-area: a3;
    }

    .layout > :global(:nth-child(5)) {
        grid-area: a4;
    }

    .layout > :global(:nth-child(6)) {
        grid-area: a5;
    }

    .layout > :global(:nth-child(7)) {
        grid-area: a6;
    }

    .layout > :global(:nth-child(8)) {
        grid-area: a7;
    }

    .layout > :global(:nth-child(9)) {
        grid-area: a8;
    }

    .layout > :global(:nth-child(10)) {
        grid-area: a9;
    }

    /* Lower specificity */
    :where(.layout) :global(:where(.layout > *)) {
        min-height: 0;
        overflow-y: hidden;
    }

    .handle {
        place-self: flex-end;
        width: 100%;
        position: relative;

        cursor: ns-resize;
        color: transparent;

        &::before {
            content: "";
            position: absolute;
            inset: 0;
            top: -1px;
            bottom: -1px;
            background-color: var(--gap-color);
        }

        &::after {
            content: "";
            position: absolute;
            inset: 0;
            top: -4px;
            bottom: -4px;
            background-color: currentColor;
        }

        overflow: visible;
    }

    .layout > .top {
        place-self: flex-start;
        grid-area: unset;
        grid-row: 1;
    }

    .layout > .bottom {
        place-self: flex-end;
        grid-area: unset;
        grid-row: -1;
    }

    .panel {
        display: grid;
        grid-template:
          "header" max-content
          "content" 1fr
          / 1fr;
    }

</style>
